/** Project-scoped WebSocket client with reconnect and prompt queuing. */

import { captureException } from "@/lib/sentry";
import { isTauri } from "@/lib/is-tauri";
import { useCompanionStore } from "@/stores/companion-store";
import type { SendPromptArgs } from "@/api/ws-actions";
import { withClientProtocol } from "./client-protocol";
import { WsOutageClock } from "./ws-outage-clock";
import { desktopApiOrigin } from "@/lib/release-channel";

const EXPECTED_CLOSE_CODES = new Set([1000, 1001, 1005]);

type MessageHandler = (data: WsMessage) => void;

export interface WsMessage {
  type: string;
  data: Record<string, unknown>;
  timestamp?: string;
  /** Server-derived ownership, never supplied by the sending client. */
  device_local?: boolean;
}

export interface WsOptions {
  onMessage: MessageHandler;
  onStatusChange: (status: ConnectionStatus) => void;
}

export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "failed";

const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 30000;
const PING_INTERVAL_MS = 30_000;
const PONG_TIMEOUT_MS = 10_000;
const MAX_QUEUED_PROMPTS = 25;

export class DenkerWebSocket {
  private ws: WebSocket | null = null;
  private connectionGeneration = 0;
  private opening = false;
  private onMessage: MessageHandler;
  private onStatusChange: (status: ConnectionStatus) => void;
  private retryCount = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private outage = new WsOutageClock(() => this.emitStatus("failed"));
  private intentionalClose = false;
  private hasConnectedBefore = false;
  private visibilityHandler: (() => void) | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pongTimer: ReturnType<typeof setTimeout> | null = null;
  private subscribedProjects = new Set<string>();
  private queuedPrompts: WsMessage[] = [];

  constructor(options: WsOptions) {
    this.onMessage = options.onMessage;
    this.onStatusChange = options.onStatusChange;
  }

  private emitStatus(status: ConnectionStatus): void {
    this.onStatusChange(status);
  }

  /** A pending attempt keeps reading "failed" once the outage ran too long. */
  private emitRetrying(): void {
    if (this.outage.failed) this.emitStatus("failed");
    else
      this.emitStatus(this.hasConnectedBefore ? "reconnecting" : "connecting");
  }

  private isCurrentGeneration(generation: number): boolean {
    return generation === this.connectionGeneration && !this.intentionalClose;
  }

  private ownsSocket(generation: number, socket: WebSocket): boolean {
    return generation === this.connectionGeneration && this.ws === socket;
  }

  connect(): void {
    if (
      this.opening ||
      (this.ws !== null &&
        (this.ws.readyState === WebSocket.CONNECTING ||
          this.ws.readyState === WebSocket.OPEN))
    ) {
      return;
    }

    this.intentionalClose = false;
    this.emitRetrying();

    const generation = ++this.connectionGeneration;
    this.opening = true;
    void this.openSocket(generation);
  }

  private async openSocket(generation: number): Promise<void> {
    const apiBase = desktopApiOrigin().replace(/\/$/, "");
    let url: string;
    if (isTauri() && apiBase && /^https?:\/\//i.test(apiBase)) {
      const wsBase = apiBase.replace(/^http/i, "ws");
      url = `${wsBase}/ws/user/`;
    } else {
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      url = `${protocol}//${window.location.host}/ws/user/`;
    }

    // /ws/user/ is wrapped in Channels' AuthMiddlewareStack which
    // expects a Django session cookie. The Tauri webview has no
    // session cookie — only a Bearer token in Keychain — so we
    // append it as `?token=<dck_*>`. Backend's
    // `BearerOrSessionAuthMiddleware` resolves the user from that
    // token before delegating to AuthMiddlewareStack.
    if (isTauri()) {
      try {
        const { invoke } = await import("@tauri-apps/api/core");
        if (!this.isCurrentGeneration(generation)) return;
        const token = await invoke<string | null>("get_auth_token").catch(
          () => null,
        );
        if (!this.isCurrentGeneration(generation)) return;
        if (token) {
          const sep = url.includes("?") ? "&" : "?";
          url = `${url}${sep}token=${encodeURIComponent(token)}`;
        }
      } catch {
        if (!this.isCurrentGeneration(generation)) return;
        // Tauri IPC unavailable (web build path) — fall through to
        // session-cookie auth.
      }
    }

    if (!this.isCurrentGeneration(generation)) return;

    const socketUrl = new URL(withClientProtocol(url));
    socketUrl.searchParams.set("denker_device_routing", "1");
    // `project_id` on prompts is only the viewed project, a hint: the server
    // routes by conversation, else to Denker's conversation in Home.
    socketUrl.searchParams.set("denker_home_routing", "1");
    const socket = new WebSocket(socketUrl.toString());
    this.ws = socket;
    this.opening = false;

    socket.onopen = () => {
      if (!this.ownsSocket(generation, socket)) {
        socket.close();
        return;
      }
      this.retryCount = 0;
      this.hasConnectedBefore = true;
      this.outage.stop();
      this.emitStatus("connected");
      this.listenVisibility();
      this.startPing();
      // Restore canonical and installed-client compatibility subscriptions.
      // CRITICAL: must happen BEFORE returning so any subsequent rawSend
      // arrives AFTER the subscribe in the Channels per-channel FIFO queue.
      for (const projectId of this.subscribedProjects) {
        this.rawSend({
          type: "project.subscribe",
          data: { project_id: projectId },
        });
      }
      this.flushQueuedPrompts();
    };

    socket.onmessage = (event: MessageEvent) => {
      if (!this.ownsSocket(generation, socket)) return;
      try {
        const msg = JSON.parse(event.data as string) as WsMessage;
        if (msg.type === "pong") {
          this.clearPongTimeout();
          return;
        }
        this.onMessage(msg);
        if (this.isDesktopCompanionConnectedEvent(msg)) {
          this.flushQueuedPrompts();
        }
      } catch {
        // Ignore unparseable messages
      }
    };

    socket.onclose = (event: CloseEvent) => {
      if (!this.ownsSocket(generation, socket)) return;
      this.ws = null;
      this.stopPing();
      if (!this.intentionalClose) {
        if (!EXPECTED_CLOSE_CODES.has(event.code)) {
          captureException(
            new Error(
              `WebSocket closed unexpectedly: ${event.code} ${event.reason}`,
            ),
            { code: event.code, reason: event.reason },
          );
        }
        this.scheduleReconnect();
      } else {
        this.emitStatus("disconnected");
      }
    };

    socket.onerror = () => {
      if (!this.ownsSocket(generation, socket)) return;
      // onclose fires after onerror, so reconnect logic is there
    };
  }

  /** Subscribe and automatically restore membership after reconnect. */
  subscribeProject(projectId: string): void {
    this.subscribedProjects.add(projectId);
    this.rawSend({
      type: "project.subscribe",
      data: { project_id: projectId },
    });
  }

  /** Unsubscribe from a project — server leaves the project group. Idempotent. */
  unsubscribeProject(projectId: string): void {
    this.subscribedProjects.delete(projectId);
    this.rawSend({
      type: "project.unsubscribe",
      data: { project_id: projectId },
    });
  }

  /** Send raw; prompts queue while the connection or companion is unavailable. */
  private rawSend(message: WsMessage): void {
    if (
      this.ws?.readyState !== WebSocket.OPEN ||
      this.shouldWaitForDesktopCompanion(message)
    ) {
      if (message.type === "prompt.send" && !this.intentionalClose) {
        this.queuedPrompts.push(message);
        if (this.queuedPrompts.length > MAX_QUEUED_PROMPTS) {
          this.queuedPrompts.shift();
        }
      }
      return;
    }
    this.ws.send(JSON.stringify(message));
  }

  private flushQueuedPrompts(): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    if (isTauri() && useCompanionStore.getState().status !== "connected") {
      return;
    }
    const queued = this.queuedPrompts.splice(0);
    for (const message of queued) {
      this.ws.send(JSON.stringify(message));
    }
  }

  private shouldWaitForDesktopCompanion(message: WsMessage): boolean {
    return (
      message.type === "prompt.send" &&
      isTauri() &&
      useCompanionStore.getState().status !== "connected"
    );
  }

  private isDesktopCompanionConnectedEvent(message: WsMessage): boolean {
    if (!isTauri() || message.type !== "companion.status") return false;
    const kind = message.data.kind;
    if (kind && kind !== "desktop") return false;
    return message.data.status === "connected";
  }

  send(message: WsMessage): void {
    this.rawSend(message);
  }

  sendPrompt(args: SendPromptArgs): void {
    const {
      projectId,
      spaceId,
      text,
      viewportCenter,
      imageIds,
      maxParallelAgents,
      mentionedAgents,
      mentionedFrames,
      conversationId,
      runtimeSessionId,
      runtimeGeneration,
      selectionGeneration,
      originDisplayId,
      promptSource,
      conversationFrameId,
      clientRequestId,
      startNewConversation,
      topicSplit,
      voiceAttention,
      screenRecordings,
      onboardingLesson,
    } = args;
    this.send({
      type: "prompt.send",
      data: {
        project_id: projectId ?? spaceId,
        ...(args.nativeChildInstruction
          ? { native_child_instruction: true }
          : {}),
        ...(args.awaitingInputId
          ? { awaiting_input_id: args.awaitingInputId }
          : {}),
        text,
        // A new prompt is addressed by Conversation identity. The backend
        // creates a fresh Execution for the accepted round-trip, so carrying
        // the previous turn's execution id would be a legacy route and is
        // intentionally never serialized here.
        // A selected chat is addressed by durable Conversation id. The
        // runtime Session is supplemental and is never sent as the chat
        // address when Conversation identity is available.
        ...(conversationId !== undefined
          ? { conversation_id: conversationId }
          : {}),
        ...(runtimeSessionId !== undefined
          ? { runtime_session_id: runtimeSessionId }
          : {}),
        ...(runtimeGeneration !== undefined
          ? { runtime_generation: runtimeGeneration }
          : {}),
        ...(selectionGeneration !== undefined
          ? { selection_generation: selectionGeneration }
          : {}),
        viewport_center: viewportCenter,
        surface: isTauri() ? "desktop" : "web",
        ...(imageIds && imageIds.length > 0 ? { image_ids: imageIds } : {}),
        ...(maxParallelAgents != null
          ? { max_parallel_agents: maxParallelAgents }
          : {}),
        ...(mentionedAgents && mentionedAgents.length > 0
          ? { mentioned_agents: mentionedAgents }
          : {}),
        ...(mentionedFrames && mentionedFrames.length > 0
          ? { mentioned_frames: mentionedFrames }
          : {}),
        ...(typeof originDisplayId === "number"
          ? { origin_display_id: originDisplayId }
          : {}),
        ...(promptSource ? { prompt_source: promptSource } : {}),
        ...(conversationFrameId
          ? { conversation_frame_id: conversationFrameId }
          : {}),
        ...(clientRequestId ? { client_request_id: clientRequestId } : {}),
        ...(startNewConversation ? { start_new_conversation: true } : {}),
        ...(topicSplit ? { topic_split: true } : {}),
        ...(voiceAttention ? { voice_attention: voiceAttention } : {}),
        ...(screenRecordings ? { screen_recordings: screenRecordings } : {}),
        ...(onboardingLesson ? { onboarding_lesson: onboardingLesson } : {}),
      },
    });
  }

  stopExecution(executionId: string): void {
    this.send({
      type: "execution.stop",
      data: { execution_id: executionId },
    });
  }

  resumeExecution(executionId: string): void {
    this.send({
      type: "execution.resume",
      data: { execution_id: executionId },
    });
  }

  /** Tell the companion to re-read commits/PR/CI for a picker-selected branch. */
  sendGithubFrameViewBranch(
    projectId: string,
    frameId: string,
    branch: string,
  ): void {
    this.send({
      type: "github_frame.view_branch",
      data: { project_id: projectId, frame_id: frameId, branch },
    });
  }

  /** Reset retry count and attempt to reconnect immediately. */
  manualReconnect(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.stopPing();
    this.connectionGeneration++;
    this.opening = false;
    const socket = this.ws;
    this.ws = null;
    if (socket) {
      socket.close();
    }
    this.retryCount = 0;
    this.connect();
  }

  disconnect(): void {
    this.connectionGeneration++;
    this.opening = false;
    this.intentionalClose = true;
    this.removeVisibilityListener();
    this.stopPing();
    this.outage.stop();
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    const socket = this.ws;
    this.ws = null;
    if (socket) {
      socket.close();
    }
    this.emitStatus("disconnected");
  }

  /**
   * When the tab becomes visible again, check if the socket is still alive.
   * Browsers may silently kill the connection while the tab is backgrounded
   * without firing onclose, so we force a reconnect if the socket isn't OPEN.
   */
  private listenVisibility(): void {
    this.removeVisibilityListener();
    this.visibilityHandler = () => {
      if (document.visibilityState !== "visible") return;
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.manualReconnect();
      }
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  private removeVisibilityListener(): void {
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState !== WebSocket.OPEN) return;
      this.ws.send(JSON.stringify({ type: "ping", data: {} }));
      this.pongTimer = setTimeout(() => {
        // No pong received — connection is dead, force reconnect
        this.ws?.close();
      }, PONG_TIMEOUT_MS);
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
    this.clearPongTimeout();
  }

  private clearPongTimeout(): void {
    if (this.pongTimer) {
      clearTimeout(this.pongTimer);
      this.pongTimer = null;
    }
  }

  private scheduleReconnect(): void {
    this.outage.start();
    const baseDelay = Math.min(
      BASE_DELAY_MS * Math.pow(2, this.retryCount),
      MAX_DELAY_MS,
    );
    // Add jitter to avoid thundering herd on reconnect
    const delay = baseDelay * (0.5 + Math.random() * 0.5);
    this.retryCount++;
    this.emitRetrying();

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connect();
    }, delay);
  }
}
